const allowedRichTextElements = new Set([
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'EM',
  'H2',
  'H3',
  'H4',
  'I',
  'LI',
  'OL',
  'P',
  'STRONG',
  'UL',
]);

const safeTextStyleProperties = new Set([
  'background-color',
  'color',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'text-align',
  'text-decoration',
]);

type SanitizeRichTextOptions = {
  preserveSafeInlineStyles?: boolean;
  preserveBlockBoundaries?: boolean;
};

const normalizedBlockElements = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FOOTER',
  'H1',
  'H5',
  'H6',
  'HEADER',
  'MAIN',
  'PRE',
  'SECTION',
  'TABLE',
  'TBODY',
  'THEAD',
  'TFOOT',
  'TR',
]);

function preserveBlockBoundary(element: Element): boolean {
  if (element.tagName === 'TD' || element.tagName === 'TH') {
    element.replaceWith(...element.childNodes, element.ownerDocument.createTextNode(' '));
    return true;
  }
  if (!normalizedBlockElements.has(element.tagName)) return false;
  const block = element.ownerDocument.createElement('div');
  block.append(...element.childNodes);
  element.replaceWith(block);
  return true;
}

function safeColor(value: string): boolean {
  return (
    /^#[\da-f]{3,8}$/iu.test(value) ||
    /^rgba?\([\d\s.,%]+\)$/iu.test(value) ||
    /^hsla?\([\d\s.,%]+\)$/iu.test(value) ||
    /^[a-z]+$/iu.test(value)
  );
}

function safeFontSize(value: string): boolean {
  const match = /^(\d+(?:\.\d+)?)(em|px|rem|%)$/iu.exec(value);
  if (match === null) return false;
  const amount = Number(match[1]);
  const unit = match[2].toLowerCase();
  if (!Number.isFinite(amount) || amount < 0) return false;
  if (unit === 'px') return amount <= 128;
  if (unit === '%') return amount <= 800;
  return amount <= 8;
}

function safeTextStyle(property: string, value: string): boolean {
  if (!safeTextStyleProperties.has(property)) return false;
  if (property === 'background-color' || property === 'color') return safeColor(value);
  if (property === 'font-size') return safeFontSize(value);
  if (property === 'font-family') return /^[-a-z\d '"_,]+$/iu.test(value);
  if (property === 'font-style') return /^(italic|normal|oblique)$/iu.test(value);
  if (property === 'font-weight') return /^(bold|normal|[1-9]00)$/iu.test(value);
  if (property === 'text-align') return /^(center|justify|left|right)$/iu.test(value);
  return /^(line-through|none|underline)(\s+(line-through|underline))?$/iu.test(value);
}

function sanitizedInlineStyle(value: string | null): string | null {
  if (value === null) return null;
  const declarations = value
    .split(';')
    .map((declaration) => declaration.trim())
    .filter((declaration) => declaration.length > 0)
    .flatMap((declaration) => {
      const separator = declaration.indexOf(':');
      if (separator < 1) return [];
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const propertyValue = declaration.slice(separator + 1).trim();
      return safeTextStyle(property, propertyValue) ? [`${property}: ${propertyValue}`] : [];
    });
  return declarations.length === 0 ? null : declarations.join('; ');
}

function safeExternalUrl(value: string | null): string | null {
  if (value === null) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : null;
  } catch {
    return null;
  }
}

export function sanitizeRichTextHtml(
  html: string,
  options: SanitizeRichTextOptions = {}
): string | null {
  if (typeof DOMParser === 'undefined') return null;
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  const sanitize = (element: Element) => {
    for (const child of [...element.children]) sanitize(child);
    if (['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT'].includes(element.tagName)) {
      element.remove();
      return;
    }
    if (options.preserveBlockBoundaries === true && preserveBlockBoundary(element)) return;
    const isSafeStyledSpan =
      options.preserveSafeInlineStyles === true && element.tagName === 'SPAN';
    if (!allowedRichTextElements.has(element.tagName) && !isSafeStyledSpan) {
      element.replaceWith(...element.childNodes);
      return;
    }
    const href = element.tagName === 'A' ? safeExternalUrl(element.getAttribute('href')) : null;
    const style =
      options.preserveSafeInlineStyles === true
        ? sanitizedInlineStyle(element.getAttribute('style'))
        : null;
    for (const attribute of [...element.attributes]) element.removeAttribute(attribute.name);
    if (style !== null) element.setAttribute('style', style);
    if (element.tagName !== 'A') return;
    if (href === null) {
      element.replaceWith(...element.childNodes);
      return;
    }
    element.setAttribute('href', href);
    element.setAttribute('target', '_blank');
    element.setAttribute('rel', 'noreferrer');
  };
  for (const child of [...body.children]) sanitize(child);
  return body.innerHTML;
}
